import isEmpty from 'lodash/isEmpty';
import { useState, useEffect } from 'react';
import { ProjectId, ProjectConversationId } from 'api/models';
import { useProjectConversationsListUpdates } from './useProjectConversationsListUpdates';
import { useProjectConversationSocketEvents } from './useProjectConversationSocketEvents';

interface ConvItemUpdates {
  [convId: number]: {
    unreadCount: number;
    countedComments: number[];
  };
}

export const useProjectConversationContentUpdates = (
  projectId: ProjectId,
  activeConvId?: ProjectConversationId,
): ConvItemUpdates => {
  const { conversations = [], isConversationsLoading } =
    useProjectConversationsListUpdates(projectId);
  const [convItemUpdates = {}, setConversationItemUpdates] =
    useState<ConvItemUpdates>();
  const data = useProjectConversationSocketEvents(projectId);
  const [, activeConversationId] = activeConvId || [];

  useEffect(() => {
    if (
      isConversationsLoading ||
      !conversations.length ||
      !isEmpty(convItemUpdates)
    ) {
      return;
    }

    const unreadMessagesInfo = conversations.reduce((acc, conv) => {
      return {
        ...acc,
        [conv.conversationId]: {
          unreadCount: conv.unreadMessages || 0,
          countedComments: [],
        },
      };
    }, {});

    setConversationItemUpdates(unreadMessagesInfo);
  }, [conversations, isConversationsLoading, convItemUpdates]);

  useEffect(() => {
    const convId = data?.commentContext.id;

    // Ignore if we already counted a comment added event
    if (
      !data ||
      !convId ||
      convItemUpdates[convId]?.countedComments.includes(data.commentId)
    ) {
      return;
    }

    // Ignore activeConvId unread counts

    if (convId !== activeConversationId) {
      const unread = convItemUpdates[convId]
        ? convItemUpdates[convId].unreadCount + 1
        : 1;

      setConversationItemUpdates({
        ...convItemUpdates,
        [convId]: {
          unreadCount: unread,
          countedComments: convItemUpdates[convId]
            ? [...convItemUpdates[convId].countedComments, data.commentId]
            : [data.commentId],
        },
      });
    } else {
      // else remember the socket comment
      setConversationItemUpdates({
        ...convItemUpdates,
        [convId]: {
          unreadCount: convItemUpdates[convId]
            ? convItemUpdates[convId].unreadCount
            : 0,
          countedComments: convItemUpdates[convId]
            ? [...convItemUpdates[convId].countedComments, data.commentId]
            : [data.commentId],
        },
      });
    }
  }, [data, convItemUpdates, activeConversationId]);

  useEffect(() => {
    if (
      activeConversationId &&
      convItemUpdates[activeConversationId] &&
      convItemUpdates[activeConversationId].unreadCount !== 0
    ) {
      setConversationItemUpdates({
        ...convItemUpdates,
        [activeConversationId]: {
          unreadCount: 0,
          countedComments:
            convItemUpdates[activeConversationId].countedComments,
        },
      });
    }
  }, [activeConversationId, convItemUpdates]);

  return convItemUpdates;
};
